
import { useEffect } from "react";
import { useSessionContext } from "@supabase/auth-helpers-react";
import { supabase } from "@/integrations/supabase/client";
import type { RealEstateSearchPreferences } from "@/types/realEstate";
import { SearchInput } from "./filters/SearchInput";
import { PropertyTypeSelect } from "./filters/PropertyTypeSelect";
import { SortSelect } from "./filters/SortSelect";
import { RangeSliders } from "./filters/RangeSliders";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { SlidersHorizontal, Filter, X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface PropertyFiltersProps {
  propertyType: string;
  setPropertyType: (value: string) => void;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  minInvestment: number;
  setMinInvestment: (value: number) => void;
  expectedReturns: number;
  setExpectedReturns: (value: number) => void;
  sortBy: string;
  setSortBy: (value: string) => void;
}

export const PropertyFilters = ({
  propertyType,
  setPropertyType,
  searchQuery,
  setSearchQuery,
  minInvestment,
  setMinInvestment,
  expectedReturns,
  setExpectedReturns,
  sortBy,
  setSortBy,
}: PropertyFiltersProps) => {
  const { session } = useSessionContext();
  const isMobile = useIsMobile();

  useEffect(() => {
    const savePreferences = async () => {
      if (!session?.user?.id) return;

      const preferences: RealEstateSearchPreferences = {
        property_type: propertyType,
        min_investment: minInvestment,
        max_investment: expectedReturns * 10000, // Convert the slider value to actual price
        expected_returns: null,
        sort_by: sortBy as RealEstateSearchPreferences['sort_by'],
        sort_order: 'desc'
      };

      const { error } = await supabase
        .from('real_estate_search_preferences')
        .upsert({
          user_id: session.user.id,
          preferences: JSON.stringify(preferences)
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error('Error saving preferences:', error);
      }
    };

    savePreferences();
  }, [propertyType, minInvestment, expectedReturns, sortBy, session?.user?.id]);

  useEffect(() => {
    const loadPreferences = async () => {
      if (!session?.user?.id) return;

      const { data, error } = await supabase
        .from('real_estate_search_preferences')
        .select('preferences')
        .eq('user_id', session.user.id)
        .single();

      if (error) {
        console.error('Error loading preferences:', error);
        return;
      }

      if (data?.preferences) {
        const prefs = JSON.parse(data.preferences as string) as RealEstateSearchPreferences;
        setPropertyType(prefs.property_type || 'all');
        setMinInvestment(prefs.min_investment || 0);
        
        // Convert the max price back to slider value
        if (prefs.max_investment) {
          setExpectedReturns(Math.floor(prefs.max_investment / 10000));
        } else {
          setExpectedReturns(0);
        }
        
        setSortBy(prefs.sort_by || 'recent');
      }
    };

    loadPreferences();
  }, [session?.user?.id]);

  const resetFilters = () => {
    setPropertyType("all");
    setSearchQuery("");
    setMinInvestment(0);
    setExpectedReturns(0);
    setSortBy("recent");
  };

  const FiltersContent = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold text-slate-800">Find Your Dream Property</h3>
        {(propertyType !== "all" || searchQuery || minInvestment > 0 || expectedReturns > 0 || sortBy !== "recent") && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={resetFilters}
            className="text-slate-500 hover:text-slate-700"
          >
            <X className="h-4 w-4 mr-1" />
            Reset Filters
          </Button>
        )}
      </div>
      
      <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-4`}>
        <SearchInput searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        <PropertyTypeSelect propertyType={propertyType} setPropertyType={setPropertyType} />
        <SortSelect sortBy={sortBy} setSortBy={setSortBy} />
      </div>

      <div className="pt-2">
        <RangeSliders
          minInvestment={minInvestment}
          setMinInvestment={setMinInvestment}
          expectedReturns={expectedReturns}
          setExpectedReturns={setExpectedReturns}
        />
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm p-4 border-b">
        <div className="flex items-center gap-4">
          <SearchInput searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="relative">
                <Filter className="h-4 w-4" />
                {(propertyType !== "all" || minInvestment > 0 || expectedReturns > 0 || sortBy !== "recent") && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-purple-500 rounded-full" />
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-[80vh]">
              <div className="pt-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Property Filters</h3>
                  <Button variant="ghost" size="sm" onClick={resetFilters}>
                    <X className="h-4 w-4 mr-1" />
                    Reset
                  </Button>
                </div>
                <PropertyTypeSelect propertyType={propertyType} setPropertyType={setPropertyType} />
                <div className="mt-4">
                  <SortSelect sortBy={sortBy} setSortBy={setSortBy} />
                </div>
                <div className="mt-6">
                  <RangeSliders
                    minInvestment={minInvestment}
                    setMinInvestment={setMinInvestment}
                    expectedReturns={expectedReturns}
                    setExpectedReturns={setExpectedReturns}
                  />
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-8">
      <FiltersContent />
    </div>
  );
};
